/**
 * House motion tokens (Task #7899) — single source of truth for Framer
 * Motion transitions across the storefront.
 *
 * Rules for future work:
 *  - UI transitions use ONE shared spring — `HOUSE_SPRING` (spring,
 *    bounce 0, ~0.35s settle) — instead of ad-hoc `tween + delay` stacks.
 *    Pass a `delay` only for entrance stagger, and on critical feedback
 *    screens (e.g. the post-payment confirmation) keep the TOTAL stagger
 *    within ~300ms so the outcome reads immediately.
 *  - Reduced motion: the CSS keyframes in `index.css` are already guarded
 *    with `prefers-reduced-motion`; the JS layer must match. Call
 *    `useReducedMotion()` and collapse translate/scale entrances to a
 *    short opacity fade (`REDUCED_MOTION_FADE`). `entranceProps()` does
 *    this for the common fade-and-rise pattern.
 *  - Never animate layout-affecting properties on hot paths (no
 *    `transition-all` on buttons / fixed chrome) — stick to transform,
 *    opacity and colors.
 */
import type { Transition, TargetAndTransition } from "framer-motion";

// Re-exported so pages get the SSR/test-safe matchMedia implementation and
// framer-motion mocks in tests don't need to stub a hook.
export { useReducedMotion } from "@/components/ui/rolling-number";

/** House default for every Framer Motion UI transition. */
export const HOUSE_SPRING: Transition = { type: "spring", bounce: 0, duration: 0.35 };

/** Short opacity-only fade used when the OS asks for reduced motion. */
export const REDUCED_MOTION_FADE: Transition = { duration: 0.15 };

/**
 * Standard entrance (fade + rise/slide/scale) that respects reduced motion.
 * With `reduced` true the translation/scale is dropped entirely and the
 * element simply fades in quickly.
 */
export function entranceProps(
  reduced: boolean,
  opts: { y?: number; x?: number; scale?: number; delay?: number } = {},
): { initial: TargetAndTransition; animate: TargetAndTransition; transition: Transition } {
  if (reduced) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: REDUCED_MOTION_FADE,
    };
  }
  return {
    initial: {
      opacity: 0,
      ...(opts.y != null ? { y: opts.y } : {}),
      ...(opts.x != null ? { x: opts.x } : {}),
      ...(opts.scale != null ? { scale: opts.scale } : {}),
    },
    animate: {
      opacity: 1,
      ...(opts.y != null ? { y: 0 } : {}),
      ...(opts.x != null ? { x: 0 } : {}),
      ...(opts.scale != null ? { scale: 1 } : {}),
    },
    transition: { ...HOUSE_SPRING, ...(opts.delay ? { delay: opts.delay } : {}) },
  };
}

/**
 * Scroll-reveal (`whileInView`) variant of `entranceProps` — collapses to a
 * short opacity fade under reduced motion.
 */
export function scrollRevealProps(
  reduced: boolean,
  opts: { y?: number; amount?: number } = {},
): {
  initial: TargetAndTransition;
  whileInView: TargetAndTransition;
  viewport: { once: boolean; amount: number };
  transition: Transition;
} {
  return {
    initial: reduced ? { opacity: 0 } : { opacity: 0, y: opts.y ?? 24 },
    whileInView: reduced ? { opacity: 1 } : { opacity: 1, y: 0 },
    viewport: { once: true, amount: opts.amount ?? 0.2 },
    transition: reduced ? REDUCED_MOTION_FADE : HOUSE_SPRING,
  };
}
